<template>
  <div class="p-6">
    <h2 class="text-lg font-semibold mb-5" :class="isDark ? 'text-white' : 'text-gray-800'">随机生成字符串</h2>

    <div class="space-y-5">
      <!-- 字符串长度 -->
      <div>
        <label class="block text-xs font-medium mb-2" :class="isDark ? 'text-gray-400' : 'text-gray-600'">
          字符串长度
        </label>
        <div class="flex items-center gap-3">
          <input
            type="range"
            v-model.number="length"
            min="1"
            max="256"
            class="flex-1 h-1.5 rounded-lg appearance-none cursor-pointer"
            :class="isDark ? 'bg-gray-700' : 'bg-gray-200'"
          />
          <input
            v-model.number="length"
            type="number"
            min="1"
            max="256"
            class="w-16 text-center font-mono text-xs py-1 rounded-md border focus:outline-none focus:ring-1 focus:ring-green-500"
            :class="isDark ? 'bg-gray-700 text-green-400 border-gray-600' : 'bg-gray-100 text-green-600 border-gray-300'"
          />
        </div>
      </div>

      <!-- 字符类型 -->
      <div>
        <label class="block text-xs font-medium mb-2" :class="isDark ? 'text-gray-400' : 'text-gray-600'">字符类型</label>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-2">
          <label v-for="opt in options" :key="opt.key"
                 class="flex items-center gap-2 px-2.5 py-1.5 rounded-md border cursor-pointer transition-all"
                 :class="opt.checked
                   ? (isDark ? 'border-green-500/60 bg-green-500/10' : 'border-green-500 bg-green-50')
                   : (isDark ? 'border-gray-700 bg-gray-800/50 hover:border-gray-600' : 'border-gray-200 bg-white hover:border-gray-300')">
            <input type="checkbox" v-model="opt.checked" class="w-3.5 h-3.5 accent-green-500" />
            <span class="text-xs" :class="isDark ? 'text-gray-300' : 'text-gray-700'">{{ opt.label }}</span>
          </label>
        </div>
        <!-- 字符枚举展示 -->
        <div v-if="symbolChecked" class="mt-2 px-3 py-2 rounded-md text-xs font-mono break-all"
             :class="isDark ? 'bg-gray-800/60 text-gray-400' : 'bg-gray-50 text-gray-500'">
          <span class="text-[10px]" :class="isDark ? 'text-gray-500' : 'text-gray-400'">特殊字符集合：</span>
          {{ symbolChars }}
        </div>
      </div>

      <!-- 排除字符 + 自定义字符（持久化） -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <label class="text-xs font-medium" :class="isDark ? 'text-gray-400' : 'text-gray-600'">字符配置</label>
          <button
            @click="clearConfig"
            class="px-2 py-0.5 text-[11px] rounded transition-all"
            :class="isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
          >
            清除配置
          </button>
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-[11px] mb-1.5" :class="isDark ? 'text-gray-500' : 'text-gray-500'">
              排除字符
            </label>
            <input
              v-model="excludeChars"
              type="text"
              placeholder="如 O0oIl1"
              class="w-full px-3 py-1.5 rounded-md border text-xs font-mono focus:outline-none focus:ring-1 focus:ring-green-500"
              :class="isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-600' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400'"
            />
          </div>
          <div>
            <label class="block text-[11px] mb-1.5" :class="isDark ? 'text-gray-500' : 'text-gray-500'">
              自定义字符
            </label>
            <input
              v-model="customChars"
              type="text"
              placeholder="如 @#$%"
              class="w-full px-3 py-1.5 rounded-md border text-xs font-mono focus:outline-none focus:ring-1 focus:ring-green-500"
              :class="isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-600' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400'"
            />
          </div>
        </div>
      </div>

      <!-- 生成数量 -->
      <div>
        <label class="block text-xs font-medium mb-2" :class="isDark ? 'text-gray-400' : 'text-gray-600'">生成数量</label>
        <div class="flex flex-wrap items-center gap-2">
          <div class="flex gap-1">
            <button
              v-for="n in [1, 5, 10]"
              :key="n"
              @click="count = n"
              class="px-2.5 py-1 rounded-md text-xs font-medium transition-all"
              :class="count === n
                ? (isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-50 text-green-700')
                : (isDark ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-800')"
            >{{ n }} 条</button>
          </div>
          <input
            v-model.number="count"
            type="number"
            min="1"
            max="1000"
            class="w-20 px-2.5 py-1 rounded-md border text-xs focus:outline-none focus:ring-1 focus:ring-green-500"
            :class="isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-800'"
          />
          <span class="text-xs" :class="isDark ? 'text-gray-500' : 'text-gray-400'">条</span>
          <button
            @click="generate"
            class="px-4 py-1 rounded-md text-xs font-medium transition-all hover:opacity-90"
            :class="isDark ? 'bg-green-500 text-white' : 'bg-green-600 text-white'"
          >
            生成
          </button>
        </div>
      </div>

      <!-- 生成结果 -->
      <div v-if="results.length" class="space-y-1.5 pt-2">
        <div class="flex items-center justify-between mb-2">
          <label class="text-xs font-medium" :class="isDark ? 'text-gray-400' : 'text-gray-600'">生成结果</label>
          <button
            @click="copyAll"
            class="px-2 py-0.5 text-[11px] rounded transition-all"
            :class="isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
          >
            {{ copiedAll ? '已复制' : '复制全部' }}
          </button>
        </div>
        <div
          v-for="(result, idx) in results"
          :key="idx"
          class="flex items-center gap-2 px-3 py-2 rounded-md border"
          :class="isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'"
        >
          <span class="text-[10px] font-mono w-6 text-center"
                :class="isDark ? 'text-gray-600' : 'text-gray-400'">{{ idx + 1 }}</span>
          <code class="flex-1 font-mono text-xs break-all" :class="isDark ? 'text-green-400' : 'text-green-600'">{{ result }}</code>
          <button
            @click="copy(result, idx)"
            class="px-2 py-0.5 text-[11px] rounded transition-all"
            :class="isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
          >
            {{ copiedIndex === idx ? '已复制' : '复制' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useTheme } from '../../composables/useTheme'

const STORAGE_KEY = 'randstr_config'

function readConfig() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch (e) {
    // ignore parse errors
  }
  return null
}

function writeConfig(value) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
}

function removeConfig() {
  localStorage.removeItem(STORAGE_KEY)
}

export default {
  name: 'RandomStringGenerator',
  setup() {
    const { isDark } = useTheme()

    const saved = readConfig() || {}
    const length = ref(16)
    const count = ref(5)
    const customChars = ref(saved.customChars || '')
    const excludeChars = ref(saved.excludeChars || '')
    const results = ref([])
    const copiedIndex = ref(-1)
    const copiedAll = ref(false)

    const symbolChars = '!@#$%^&*()_+-=[]{}|;:,.<>?'

    const options = ref([
      { key: 'upper', label: '大写字母', checked: true, chars: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ' },
      { key: 'lower', label: '小写字母', checked: true, chars: 'abcdefghijklmnopqrstuvwxyz' },
      { key: 'number', label: '数字', checked: true, chars: '0123456789' },
      { key: 'symbol', label: '特殊字符', checked: false, chars: symbolChars }
    ])

    const symbolChecked = computed(() => options.value.find(o => o.key === 'symbol')?.checked)

    // 持久化排除字符和自定义字符到 localStorage
    watch([excludeChars, customChars], () => {
      writeConfig({ excludeChars: excludeChars.value, customChars: customChars.value })
    })

    // 限制长度输入范围
    watch(length, (v) => {
      if (!v || v < 1) length.value = 1
      else if (v > 256) length.value = 256
    })

    const clearConfig = () => {
      removeConfig()
      excludeChars.value = ''
      customChars.value = ''
    }

    const availableChars = computed(() => {
      let chars = ''
      options.value.forEach(opt => {
        if (opt.checked) chars += opt.chars
      })
      if (customChars.value) chars += customChars.value
      if (excludeChars.value) {
        const excludeSet = new Set(excludeChars.value)
        chars = chars.split('').filter(c => !excludeSet.has(c)).join('')
      }
      return chars
    })

    const generate = () => {
      if (!availableChars.value) {
        alert('请至少选择一种字符类型（注意排除字符不要把所选字符全部屏蔽）')
        return
      }
      const n = Math.min(Math.max(count.value || 1, 1), 1000)
      const list = []
      const chars = availableChars.value
      for (let i = 0; i < n; i++) {
        let str = ''
        const arr = new Uint32Array(length.value)
        crypto.getRandomValues(arr)
        for (let j = 0; j < length.value; j++) {
          str += chars[arr[j] % chars.length]
        }
        list.push(str)
      }
      results.value = list
      copiedIndex.value = -1
    }

    const copy = async (text, idx) => {
      await navigator.clipboard.writeText(text)
      copiedIndex.value = idx
      setTimeout(() => { copiedIndex.value = -1 }, 1500)
    }

    const copyAll = async () => {
      await navigator.clipboard.writeText(results.value.join('\n'))
      copiedAll.value = true
      setTimeout(() => { copiedAll.value = false }, 1500)
    }

    return {
      isDark, length, count, customChars, excludeChars,
      options, results, copiedIndex, copiedAll,
      symbolChars, symbolChecked, availableChars,
      generate, copy, copyAll, clearConfig
    }
  }
}
</script>
